import React, { useState, useMemo } from 'react'
import qrcode from 'qrcode-generator'
import { colors } from './fieldTheme'
import { canEndTour, inactivityLabel, tourPath, pitchDoneCount } from './fieldTours'
import { shortName, doneCountLabel } from './fieldLabels'
import { nowSeconds } from './fieldDates'
import { ClearIcon, CheckIcon } from './FieldIcons'
import { BottomSheet, SheetGroup } from './FieldUI'

// Options d'un tour en cours (canvas W4, W4d), partage (W4b) et relance après 30 jours (W4e).
// Décisions 22 et 39. Terminer sans confirmation : le message « Tour terminé » propose Annuler (W4f).

function OptionRow({ title, subtitle, icon, disabled, onClick })
{
	return (
		<button
			type="button"
			className="field-row"
			disabled={disabled}
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: '#F2F3F7', minHeight: 72, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 20px', border: 0, color: disabled ? '#5B6577' : colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer' }}
		>
			<span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
				<span style={{ fontSize: 19, fontWeight: 700 }}>{title}</span>
				<span style={{ fontSize: 15, fontWeight: 500, color: disabled ? '#5B6577' : colors.textSecondary }}>{subtitle}</span>
			</span>
			{icon}
		</button>
	)
}

const ShareIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
		<rect x="3" y="3" width="7" height="7" rx="1" />
		<rect x="14" y="3" width="7" height="7" rx="1" />
		<rect x="3" y="14" width="7" height="7" rx="1" />
		<path d="M14 14h3v3h-3zM20 14v.01M14 20h.01M17 20h4v-3" />
	</svg>
)

const FlagIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
		<path d="M5 21V4" />
		<path d="M5 4h12l-2.5 4L17 12H5" />
	</svg>
)

const HideIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
		<path d="M3 3l18 18" />
		<path d="M10.6 5.1A10 10 0 0 1 12 5c6 0 9.5 7 9.5 7a17 17 0 0 1-3 3.8" />
		<path d="M6.6 6.6A17 17 0 0 0 2.5 12S6 19 12 19a9.6 9.6 0 0 0 4.4-1" />
		<path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
	</svg>
)

const LinkIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
		<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
		<path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
	</svg>
)

// reminder : relance des 30 jours, avec « Le garder en cours » à la place d'Annuler (W4e)
export function TourOptionsSheet({ tour, user, reminder, onShare, onEnd, onHide, onKeep, onClose })
{
	const endAllowed = canEndTour(tour, user)
	const mine = user && tour.create_user === user._id
	const starter = shortName(tour.create_user_name)
	const startedBy = mine ? 'démarré par vous' + (starter ? ' (' + starter + ')' : '') : (starter ? 'démarré par ' + starter : '')
	const endReason = 'Réservé aux administrateurs'
	const title = reminder ? 'Le ' + tour.name + ' est toujours en cours' : 'Options du tour'
	const text = reminder ? inactivityLabel(tour, nowSeconds()) : [tour.name, startedBy].filter(Boolean).join(', ')

	return (
		<BottomSheet title={title} text={text} onClose={onClose}>
			<SheetGroup>
				{!reminder && <OptionRow title="Partager le tour" subtitle="Un collègue scanne et rejoint le tour" icon={<ShareIcon />} onClick={onShare} />}
				<OptionRow title="Terminer le tour" subtitle={endAllowed ? 'Il quitte l\'accueil de toute l\'équipe et reste consultable' : endReason} icon={<FlagIcon />} disabled={!endAllowed} onClick={onEnd} />
				<OptionRow title="Masquer de mon accueil" subtitle="Pour moi seulement, le tour reste accessible depuis Autres inventaires" icon={<HideIcon />} onClick={onHide} />
			</SheetGroup>
			<SheetGroup>
				{reminder
					? <OptionRow title="Le garder en cours" subtitle="Pas de nouveau rappel avant 30 jours sans ajout" icon={<CheckIcon />} onClick={onKeep} />
					: <OptionRow title="Annuler" subtitle="Ne rien changer" icon={<ClearIcon />} onClick={onClose} />}
			</SheetGroup>
		</BottomSheet>
	)
}

// QR code du lien, dessiné en SVG (bibliothèque qrcode-generator), marge blanche de 4 modules
function QrCode({ text, label, size = 220 })
{
	const path = useMemo(() => {
		const qr = qrcode(0, 'M')
		qr.addData(text)
		qr.make()
		const count = qr.getModuleCount()
		let d = ''
		for (let row = 0; row < count; row++)
		{
			for (let col = 0; col < count; col++)
			{
				if (qr.isDark(row, col))
					d += 'M' + (col + 4) + ' ' + (row + 4) + 'h1v1h-1z'
			}
		}
		return { d, box: count + 8 }
	}, [text])
	return (
		<svg width={size} height={size} viewBox={'0 0 ' + path.box + ' ' + path.box} shapeRendering="crispEdges" role="img" aria-label={label} style={{ display: 'block' }}>
			<rect width={path.box} height={path.box} fill="#FFFFFF" />
			<path d={path.d} fill="#000000" />
		</svg>
	)
}

// Partager le tour (W4b, décision 39) : un collègue scanne le QR code, ou reçoit le lien.
// Le lien ne donne aucun accès à lui seul : connexion et droit d'écriture obligatoires.
export function TourShareSheet({ tour, pitches, onClose })
{
	const [copied, setCopied] = useState(false)
	const link = (typeof window !== 'undefined' ? window.location.origin : '') + tourPath(tour)
	const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

	const copy = () => {
		const done = () => setCopied(true)
		if (navigator.clipboard && navigator.clipboard.writeText)
			navigator.clipboard.writeText(link).then(done).catch(done)
		else
			done()
	}

	return (
		<BottomSheet title="Partager le tour" text={[tour.name, doneCountLabel(pitchDoneCount(tour, pitches)).toLowerCase()].filter(Boolean).join(', ')} onClose={onClose}>
			<div style={{ display: 'flex', justifyContent: 'center' }}>
				<div style={{ background: '#FFFFFF', border: '2px solid ' + colors.separator, borderRadius: 20, padding: 10 }}>
					<QrCode text={link} label={'QR code du ' + tour.name} />
				</div>
			</div>
			<p style={{ fontSize: 17, fontWeight: 500, color: colors.text, textAlign: 'center' }}>Un collègue scanne ce code avec son téléphone et rejoint le tour. Il doit être connecté et avoir le droit de faire des inventaires.</p>
			<SheetGroup>
				{canShare && <OptionRow title="Envoyer le lien" subtitle="SMS, WhatsApp, e-mail…" icon={<ShareIcon />} onClick={() => navigator.share({ title: tour.name, url: link }).catch(() => null)} />}
				<OptionRow title={copied ? 'Lien copié' : 'Copier le lien'} subtitle={link.replace(/^https?:\/\//, '')} icon={copied ? <CheckIcon /> : <LinkIcon />} onClick={copy} />
			</SheetGroup>
			<SheetGroup>
				<OptionRow title="Fermer" subtitle="Revenir à l'accueil" icon={<ClearIcon />} onClick={onClose} />
			</SheetGroup>
		</BottomSheet>
	)
}
